import React, { ChangeEvent as ChangeInput, JSX } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { isValid } from "date-fns";

import {
	Link,
	NavigateFunction,
	Params,
	useLocation,
	useNavigate,
	useParams
} from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import UpgradeIcon from "@mui/icons-material/Upgrade";

import {
	BlueprintType,
	getBlueprintInfo
} from "core/blueprint/base/BlueprintService";
import { getBlueprintLink } from "core/blueprint/base/Utils";
import {
	EvaluationRight,
	InformationRight,
	InformationRights,
	Report,
	ReportFieldRights,
	ReportRight,
	ReportRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import {
	CompetenceListField,
	Field,
	FieldType,
	GroupField
} from "core/field/FieldService";
import { getTrajectoryReportBlueprints } from "core/report/ReportService";
import ReportView from "core/report/ReportView";
import { evaluationDates } from "evaluation/EffectiveScore";

import { getLoggedInUser } from "authentication/AuthenticationService";

import Prompt from "lib/components/prompt/Prompt";

import { stringToDate } from "utils/DateUtils";
import { ERR_INTERNET_DISCONNECTED } from "utils/ErrorCodes";
import { asTitle } from "utils/Utils";

import { SECTION } from "../document/Document";
import DocumentTabs from "../document/DocumentTabs";

import { Dashboard } from "core/blueprint/dashboard/DashboardTypes";
import AccessView from "./AccessView";
import { DashboardView } from "./DashboardView";
import EvaluationView from "./EvaluationView";
import FormView from "./FormView";
import ReportOverview from "./ReportOverview";
import {
	Evaluation,
	ReportInfo,
	TrajectoryAccess,
	TrajectoryInfo,
	TrajectoryRight,
	getTrajectoryAccess,
	getTrajectoryCompetenceList,
	getTrajectoryDashboard,
	getTrajectoryEvaluations,
	getTrajectoryForm,
	getTrajectoryInfo,
	getTrajectoryReports,
	updateBlueprintVersion,
	updateTrajectoryInfo
} from "./TrajectoryService";
import TrajectoryViewActionbar from "./TrajectoryViewActionbar";
import { Competence } from "core/blueprint/competencelist/CompetenceListService";

const PATH_REGEX = /\/trajectory\/(\d+)(?:\/(\w*))?/;
const REPORT_REGEX = /\/trajectory\/\d+\/reports\/(\d+)/;

type Form = {
	fields: Field[];
};

interface TrajectoryState {
	error: boolean;
	errorMessage: string;
	success: boolean;
	successMessage: string;
	dirty: boolean;

	tab: SECTION;

	edit: boolean;
	filterValue: string;
	// The day whose state the dashboard and the overview show; null means
	// the current state.
	asOf: Date | null;
	// True while an evaluation is being edited; the pinned row then carries
	// that evaluation's controls instead of the trajectory's.
	evaluationEditing: boolean;

	info: TrajectoryInfo | undefined;
	infoModified: boolean;

	access: TrajectoryAccess;
	dashboard: Dashboard | undefined;
	form: Form | undefined;
	evaluation: Evaluation | undefined;

	reportBlueprints: Report[];
	reports: ReportInfo[];
	openReport: number | undefined;

	blueprintName: string | null;
	latestBlueprintVersion: number | null;
	showBlueprintUpdateDialog: boolean;

	labels: Map<string, Array<string>>;
}

type RouteComponentProps = {
	location: Location;
	navigate: NavigateFunction;
	params: Readonly<Params>;
};
export type TrajectoryViewProps = RouteComponentProps & WithTranslation;

class TrajectoryView extends React.Component<
	TrajectoryViewProps,
	TrajectoryState
> {
	constructor(props: TrajectoryViewProps) {
		super(props);

		this.state = {
			error: false,
			errorMessage: "",
			success: false,
			successMessage: "",
			dirty: false,

			tab: SECTION.FIELDS,

			edit: false,
			filterValue: "",
			asOf: null,
			evaluationEditing: false,

			dashboard: undefined,
			info: undefined,
			infoModified: false,

			access: {
				roles: [],
				users: []
			},
			form: undefined,
			evaluation: undefined,

			reportBlueprints: [],
			reports: [],
			openReport: undefined,

			blueprintName: null,
			latestBlueprintVersion: null,
			showBlueprintUpdateDialog: false,

			labels: new Map<string, Array<string>>()
		};

		this.save = this.save.bind(this);
		this.edit = this.edit.bind(this);
		this.exit = this.exit.bind(this);
		this.filter = this.filter.bind(this);
		this.setAsOf = this.setAsOf.bind(this);
		this.setEvaluationEditing = this.setEvaluationEditing.bind(this);
		this.setTitle = this.setTitle.bind(this);
		this.setDescription = this.setDescription.bind(this);
		this.updateForm = this.updateForm.bind(this);
		this.updateAccess = this.updateAccess.bind(this);
		this.updateEvaluation = this.updateEvaluation.bind(this);
		this.isActive = this.isActive.bind(this);

		this.getEvaluationRights = this.getEvaluationRights.bind(this);
		this.getReportRights = this.getReportRights.bind(this);
		this.isPromptEnabled = this.isPromptEnabled.bind(this);

		this.linkedBlueprintText = this.linkedBlueprintText.bind(this);
	}

	public componentDidMount(): void {
		this.loadTrajectory();
	}

	public static getDerivedStateFromProps(props: TrajectoryViewProps) {
		const path = props.location.pathname;
		const match = path.match(PATH_REGEX);

		if (match && match[2] !== undefined) {
			const section = TrajectoryView.determineSection(match[2]);
			if (section === SECTION.REPORTS) {
				const reportMatch = path.match(REPORT_REGEX);
				if (reportMatch && reportMatch[1] !== undefined) {
					return {
						tab: section,
						openReport: parseInt(reportMatch[1])
					};
				}
			}

			return { tab: section, openReport: undefined };
		}

		return {};
	}

	private static determineSection(pathSection: string): SECTION {
		switch (pathSection) {
			case "dashboard":
				return SECTION.DASHBOARD;
			case "form":
				return SECTION.FIELDS;
			case "evaluation":
				return SECTION.EVALUATION;
			case "reports":
				return SECTION.REPORTS;
			case "access":
				return SECTION.ACCESS;
			default:
				return SECTION.ACCESS;
		}
	}

	private getPathSection(section: SECTION): string {
		switch (section) {
			case SECTION.DASHBOARD:
				return "dashboard";
			case SECTION.FIELDS:
				return "form";
			case SECTION.EVALUATION:
				return "evaluation";
			case SECTION.REPORTS:
				return "reports";
			case SECTION.ACCESS:
				return "access";
			default:
				return "form";
		}
	}

	private pathForSection(section: SECTION): string {
		const currentPath = this.props.location.pathname;
		const match = currentPath.match(PATH_REGEX);
		if (match && match[1] !== undefined) {
			return (
				"/trajectory/" + match[1] + "/" + this.getPathSection(section)
			);
		}

		return currentPath;
	}

	private loadTrajectory(): void {
		const path = this.props.location.pathname;
		const match = path.match(PATH_REGEX);
		if (!match) {
			return;
		}

		const id = parseInt(match[1]);
		getTrajectoryInfo(id).then(data => {
			this.setState({ info: data });
			if (data.blueprintId !== null) {
				getBlueprintInfo(data.blueprintId)
					.then(blueprintInfo =>
						this.setState({
							latestBlueprintVersion: blueprintInfo.version,
							blueprintName: blueprintInfo.name
						})
					)
					.catch(() => {
						// Could not load linked blueprint.
					});
			}
		});
		getTrajectoryDashboard(id).then(data => {
			this.setState({
				dashboard: {
					widgets: data
				}
			});
		});
		getTrajectoryForm(id).then(data =>
			this.setState({
				form: {
					fields: data
				}
			})
		);
		getTrajectoryReportBlueprints(id).then(data =>
			this.setState({
				reportBlueprints: data
			})
		);
		getTrajectoryReports(id).then(data =>
			this.setState({
				reports: data
			})
		);
		getTrajectoryEvaluations(id).then(data =>
			this.setState({
				evaluation: data
			})
		);
		getTrajectoryAccess(id).then(data =>
			this.setState({
				access: data
			})
		);
		getTrajectoryCompetenceList(id).then(data => {
			const labels = new Map<string, Array<string>>();
			data.competences.forEach(competence =>
				this.addLabels(competence, labels)
			);

			this.setState({ labels });
		});
	}

	private addLabels(
		competence: Competence,
		labels: Map<string, Array<string>>
	): void {
		let currentLabels = labels.get(competence.uuid);
		if (currentLabels === undefined) {
			currentLabels = [];
			labels.set(competence.uuid, currentLabels);
		}
		currentLabels.push(...competence.labels);
		// labels.set(competence.uuid, currentLabels);
		competence.subcompetences.forEach(sub => {
			labels.set(sub.uuid, competence.labels);
			this.addLabels(sub, labels);
		});
	}

	private saveInfo(): Promise<void> {
		if (this.state.info === undefined || !this.state.infoModified) {
			return Promise.resolve();
		}

		return updateTrajectoryInfo(this.state.info.id, {
			name: this.state.info.name,
			description: this.state.info.description
		});
	}

	private save(): Promise<void> {
		return this.saveInfo()
			.then(() =>
				this.setState({
					error: false,
					success: true,
					successMessage: this.props.t("saveDocumentSuccess", {
						document: "trajectory"
					}),
					dirty: false,
					infoModified: false,
					edit: false
				})
			)
			.then(() => this.loadTrajectory())
			.catch((reason: Error) => {
				let errorMessage: string;
				if (reason.message === ERR_INTERNET_DISCONNECTED) {
					errorMessage = this.props.t("internetDisconnectedError");
				} else {
					errorMessage = this.props.t("saveDocumentFailure", {
						document: "trajectory"
					});
				}

				this.setState({
					success: false,
					error: true,
					errorMessage: errorMessage
				});
			});
	}

	private edit(): void {
		this.setState({ edit: true });
	}

	private exit(): void {
		this.props.navigate("/trajectories");
	}

	private filter(value: string): void {
		this.setState({ filterValue: value });
	}

	private setAsOf(date: Date | null): void {
		// The picker reports a partially typed field as an Invalid Date on
		// every keystroke; that is no date, and format() would throw on it.
		this.setState({ asOf: date !== null && isValid(date) ? date : null });
	}

	private setEvaluationEditing(editing: boolean): void {
		this.setState({ evaluationEditing: editing });
	}

	private setTitle(value: string): void {
		const info = this.state.info;
		if (info) {
			info.name = value;
			this.setState({ info: info, infoModified: true, dirty: true });
		}
	}

	private setDescription(event: ChangeInput<HTMLTextAreaElement>): void {
		const info = this.state.info;
		if (info) {
			info.description = event.target.value;
			this.setState({ info: info, infoModified: true, dirty: true });
		}
	}

	private getUserRoles(): string[] | undefined {
		const user = getLoggedInUser();
		return this.state.access.users.find(u => u.user === user)?.roles;
	}

	private getEvaluationRights(): boolean[] {
		if (!this.state.access) {
			return [false, false];
		}

		const userRoles = this.getUserRoles();
		if (userRoles === undefined || userRoles.length === 0) {
			return [false, false];
		}

		const evaluationRights = this.state.access.roles
			.filter(role => userRoles.includes(role.name))
			.map(role => role.evaluationRights);

		return evaluationRights.reduce(
			(accumulator, value) => [
				accumulator[EvaluationRight.WRITE] ||
					value[EvaluationRight.WRITE],
				accumulator[EvaluationRight.READ] || value[EvaluationRight.READ]
			],
			[false, false]
		);
	}

	private isAllowedToEditMetadata(): boolean {
		return (
			this.state.info?.rights.includes(TrajectoryRight.METADATA) ?? false
		);
	}

	private getInformationRights(): InformationRights {
		if (!this.state.access) {
			return {};
		}

		const userRoles = this.getUserRoles();
		if (userRoles === undefined || userRoles.length === 0) {
			return {};
		}

		const informationRights = this.state.access.roles
			.filter(role => userRoles.includes(role.name))
			.map(role => role.informationRights);

		const fields = this.state.form?.fields ?? [];
		const collapsedRights: InformationRights = {};
		for (const rights of informationRights) {
			const rootRights = rights[""] ?? [false, false, false];
			const expandedRights = { ...rights };
			fields.forEach(field =>
				this.updateFieldRights(field, rootRights, expandedRights)
			);

			this.mergeInfoRightsInto(collapsedRights, expandedRights);
		}

		if (collapsedRights[""] === undefined) {
			collapsedRights[""] = [false, false, false];
		}

		collapsedRights[""][InformationRight.READ] = fields.some(
			field => collapsedRights[field.uuid][InformationRight.READ]
		);

		return collapsedRights;
	}

	private mergeInfoRightsInto(a: InformationRights, b: InformationRights) {
		Object.entries(b).forEach(right => {
			const key = right[0];

			if (!Object.keys(a).includes(key)) {
				a[key] = [false, false, false];
			}

			a[key][InformationRight.WRITE] =
				a[key][InformationRight.WRITE] ||
				b[key][InformationRight.WRITE];
			a[key][InformationRight.READ] =
				a[key][InformationRight.READ] || b[key][InformationRight.READ];

			const hasRight =
				a[key][InformationRight.WRITE] || a[key][InformationRight.READ];
			a[key][InformationRight.HIDDEN] =
				(a[key][InformationRight.HIDDEN] ||
					b[key][InformationRight.HIDDEN]) &&
				!hasRight;
		});
	}

	private updateFieldRights(
		field: Field,
		parentRights: boolean[],
		rights: InformationRights
	): void {
		const hasExplicit =
			rights[field.uuid]?.some(right => right === true) ?? false;
		const fieldRights = hasExplicit
			? rights[field.uuid]
			: [...parentRights];
		if (field.type === FieldType.GROUP) {
			const children = (field as GroupField).children;
			children.forEach(child =>
				this.updateFieldRights(child, fieldRights, rights)
			);
			fieldRights[InformationRight.READ] = children.some(
				child => rights[child.uuid][InformationRight.READ]
			);
		} else if (field.type === FieldType.COMPETENCE_LIST) {
			const levels = (field as CompetenceListField).levels;
			const elements = levels.map(level => level.elements).flat();
			elements.forEach(element =>
				this.updateFieldRights(element, fieldRights, rights)
			);
			fieldRights[InformationRight.READ] = elements.some(
				element => rights[element.uuid][InformationRight.READ]
			);
		}

		rights[field.uuid] = fieldRights;
	}

	private getReportRights(): ReportRights {
		if (!this.state.access) {
			return {};
		}

		const userRoles = this.getUserRoles();
		if (userRoles === undefined || userRoles.length === 0) {
			return {};
		}

		const reportRights: ReportRights = {};
		this.state.reportBlueprints.forEach(blueprint => {
			const name = blueprint.name;
			if (!reportRights[name]) {
				reportRights[name] = {};
			}
			reportRights[name][""] = [false, false, false, false];

			this.state.access.roles
				.filter(role => userRoles.includes(role.name))
				.forEach(role => {
					const roleBlueprints = Object.keys(role.reportRights);
					// Only if specific rights override it (have true anywhere, use it!)
					if (
						roleBlueprints.includes(name) &&
						role.reportRights[name][""]?.some(
							right => right === true
						)
					) {
						this.mergeInto(
							reportRights[name][""],
							role.reportRights[name][""]
						);
					} else if (roleBlueprints.includes("")) {
						this.mergeInto(
							reportRights[name][""],
							role.reportRights[""][""]
						);
					}

					if (roleBlueprints.includes(name)) {
						const fieldNames = Object.keys(role.reportRights[name]);
						fieldNames.forEach(f => {
							if (f !== "") {
								reportRights[name][f] =
									role.reportRights[name][f];
							}
						});
					}
				});

			const fields = blueprint.fields ?? [];
			const collapsedRights: ReportFieldRights = {};

			const allRights = reportRights[name][""].slice(1);
			const { "": _, ...expandedRights } = reportRights[name];
			fields.forEach(field =>
				this.updateFieldRights(field, allRights, expandedRights)
			);
			this.mergeInfoRightsInto(collapsedRights, expandedRights);

			const reportRolesRights = this.state.access.roles
				.filter(role => userRoles.includes(role.name))
				.filter(role => role.reportRights[name])
				.map(role => role.reportRights[name]);

			for (const rights of reportRolesRights) {
				this.mergeInfoRightsInto(collapsedRights, { ...rights });
			}

			reportRights[name] = {
				...collapsedRights,
				"": reportRights[name][""]
			};
		});

		return reportRights;
	}

	// FOR REPORT RIGHTS
	private mergeInto(a: boolean[], b: boolean[]): void {
		a[ReportRight.ADMIN] = a[ReportRight.ADMIN] || b[ReportRight.ADMIN];
		a[ReportRight.WRITE] = a[ReportRight.WRITE] || b[ReportRight.WRITE];
		a[ReportRight.READ] = a[ReportRight.READ] || b[ReportRight.READ];

		// HIDDEN
		const hasRight =
			a[ReportRight.ADMIN] || a[ReportRight.WRITE] || a[ReportRight.READ];
		a[ReportRight.HIDDEN] =
			(a[ReportRight.HIDDEN] || b[ReportRight.HIDDEN]) && !hasRight;
	}

	private isAllowedToEditAccess(): boolean {
		return (
			this.state.info?.rights.includes(TrajectoryRight.RIGHTS) ?? false
		);
	}

	private updateForm(fields: Field[]): void {
		this.setState(prevState => ({
			...prevState,
			form: {
				fields
			}
		}));
	}

	private updateAccess(access: TrajectoryAccess): void {
		this.setState(prevState => ({
			...prevState,
			access
		}));
	}

	private updateEvaluation(): Promise<void> {
		const path = this.props.location.pathname;
		const match = path.match(PATH_REGEX);
		if (!match) {
			return Promise.resolve();
		}

		const id = parseInt(match[1]);
		return getTrajectoryEvaluations(id).then(data =>
			this.setState({
				evaluation: data
			})
		);
	}

	private isActive(): boolean {
		if (this.state.info === undefined) {
			return false;
		}
		if (this.state.info.activeUntil === null) {
			return false;
		}

		const expiryDate = stringToDate(this.state.info.activeUntil);
		if (expiryDate === null) {
			return false;
		}

		const now = new Date();
		return expiryDate > now;
	}

	private isPromptEnabled(nextPath: string): boolean {
		const current = this.props.location.pathname.match(PATH_REGEX);
		const match = nextPath.match(PATH_REGEX);
		if (match && current && match[1] === current[1]) {
			return false;
		}

		return true;
	}

	private linkedBlueprintText(): JSX.Element {
		const label = asTitle(
			this.props.t("linkedBlueprint", {
				document: "trajectoryBlueprint"
			})
		);

		if (this.state.info && this.state.info.blueprintId !== null) {
			if (this.state.blueprintName === null) {
				return (
					<Grid container>
						<Grid item xs={8} sm={5} md={4} lg={3}>
							<Typography variant="subtitle1">{label}</Typography>
						</Grid>
						<Grid item xs={4} sm={7} md={8} lg={9}>
							<Typography variant="body1">
								{this.props.t("removed")}
							</Typography>
						</Grid>
					</Grid>
				);
			} else {
				return (
					<Grid container>
						<Grid item container alignItems="center">
							<Grid item xs={8} sm={5} md={4} lg={3}>
								<Typography variant="subtitle1">
									{label}
								</Typography>
							</Grid>
							<Grid item xs={4} sm={7} md={8} lg={9}>
								<Typography variant="body1">
									<Link
										to={getBlueprintLink(
											this.state.info.blueprintId,
											BlueprintType.TRAJECTORY
										)}>
										{this.state.blueprintName}
									</Link>
								</Typography>
							</Grid>
						</Grid>
						<Grid item container alignItems="center">
							<Grid item xs={8} sm={5} md={4} lg={3}>
								<Typography variant="subtitle1">
									{this.props.t("version")}
								</Typography>
							</Grid>
							<Grid
								item
								container
								xs={4}
								sm={7}
								md={8}
								lg={9}
								alignItems="center">
								<Grid item>
									<Typography variant="body1">
										{this.state.info.blueprintVersion}/
										{this.state.latestBlueprintVersion}
									</Typography>
								</Grid>
								<Grid item>
									<IconButton
										size="small"
										disabled={
											!this.state.edit ||
											this.state.info.blueprintVersion ===
												this.state
													.latestBlueprintVersion
										}
										onClick={() =>
											this.state.info?.id &&
											this.setState({
												showBlueprintUpdateDialog: true
											})
										}>
										<UpgradeIcon />
									</IconButton>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			}
		}

		return (
			<Typography variant="subtitle2">
				{`${label}: ${this.props.t("none")}`}
			</Typography>
		);
	}

	public render(): JSX.Element {
		const { t } = this.props;

		return (
			<>
				<Box sx={{ displayPrint: "none" }}>
					{this.state.error && (
						<Alert
							id="error-message"
							severity="error"
							onClose={(): void =>
								this.setState({ error: false })
							}>
							{this.state.errorMessage}
						</Alert>
					)}
					{this.state.success && (
						<Alert
							id="success-message"
							severity="success"
							onClose={(): void =>
								this.setState({ success: false })
							}>
							{this.state.successMessage}
						</Alert>
					)}
					{this.state.info && !this.isActive() && (
						<Alert severity="warning">
							{t("expiredTrajectory")}
						</Alert>
					)}
				</Box>

				{this.state.info && this.state.access && (
					<TrajectoryViewActionbar
						edit={this.state.edit}
						dirty={this.state.dirty}
						info={this.state.info}
						access={this.state.access}
						onChangeFilter={this.filter}
						onChangeTitle={this.setTitle}
						onEdit={this.edit}
						onSave={() => this.save()}
						onExit={this.exit}
						asOf={this.state.asOf}
						asOfDates={
							this.state.evaluation
								? evaluationDates(this.state.evaluation.criteria)
								: []
						}
						contentEditing={this.state.evaluationEditing}
						onChangeAsOf={this.setAsOf}
					/>
				)}

				<Grid sx={{ displayPrint: "none" }} container>
					{this.state.edit && this.isAllowedToEditMetadata() ? (
						<>
							<Grid container>
								<TextField
									id="description"
									type="text"
									label={t("description")}
									fullWidth
									multiline
									rows={3}
									inputProps={{ maxLength: 255 }}
									value={this.state.info?.description}
									onChange={this.setDescription}
									helperText={
										this.state.info?.description.length.toString() +
										"/255"
									}
									variant="standard"
								/>
							</Grid>
							<Grid container>{this.linkedBlueprintText()}</Grid>
						</>
					) : (
						<>
							<Grid container>
								<Grid item xs={6} sm={3} lg={2}>
									<Typography
										id="description-label"
										variant="subtitle1">
										{t("description")}
									</Typography>
								</Grid>
								<Grid item xs={12} sm={9} lg={10}>
									<Typography
										id="description-value"
										variant="body1">
										{this.state.info?.description}
									</Typography>
								</Grid>
							</Grid>
							<Grid container>{this.linkedBlueprintText()}</Grid>
						</>
					)}
				</Grid>

				<DocumentTabs
					activeTab={this.state.tab}
					hideDashboard={this.state.dashboard?.widgets.length === 0}
					onSelectTab={value =>
						this.props.navigate(this.pathForSection(value), {
							replace: true
						})
					}
				/>

				{this.state.tab === SECTION.DASHBOARD &&
					this.state.info &&
					this.state.dashboard &&
					this.state.evaluation && (
						<DashboardView
							dashboard={this.state.dashboard}
							evaluation={this.state.evaluation}
							asOf={this.state.asOf ?? undefined}
						/>
					)}

				{this.state.tab === SECTION.FIELDS &&
					this.state.info &&
					this.state.form && (
						<FormView
							trajectoryId={this.state.info.id}
							fields={this.state.form.fields}
							filter={this.state.filterValue}
							rights={this.getInformationRights()}
							onSaveForm={this.updateForm}
						/>
					)}

				{this.state.tab === SECTION.EVALUATION &&
					this.state.info &&
					this.state.evaluation && (
						<EvaluationView
							trajectoryId={this.state.info.id}
							evaluation={this.state.evaluation}
							filter={this.state.filterValue}
							rights={this.getEvaluationRights()}
							asOf={this.state.asOf ?? undefined}
							onSavedEvaluation={this.updateEvaluation}
							onEditingChange={this.setEvaluationEditing}
						/>
					)}

				{this.state.tab === SECTION.REPORTS &&
					this.state.info &&
					(this.state.openReport === undefined ? (
						<ReportOverview
							trajectoryId={this.state.info.id}
							rights={this.getReportRights()}
							reportBlueprints={this.state.reportBlueprints.map(
								bp => bp.name
							)}
							onCreate={reportId =>
								this.setState({ openReport: reportId })
							}
						/>
					) : (
						<ReportView
							trajectoryId={this.state.info?.id}
							reportId={this.state.openReport}
							rights={this.getReportRights()}
							labels={this.state.labels}
						/>
					))}

				{this.state.tab === SECTION.ACCESS &&
					this.state.info &&
					this.state.access && (
						<AccessView
							trajectoryId={this.state.info.id}
							access={this.state.access}
							information={this.state.form?.fields ?? []}
							reports={this.state.reportBlueprints}
							onSaveAccess={this.updateAccess}
						/>
					)}

				{this.state.edit && (
					<Prompt
						when={this.state.dirty}
						message={t("exitDocumentMessage")}
						condition={this.isPromptEnabled}
					/>
				)}

				<Dialog
					open={this.state.showBlueprintUpdateDialog}
					onClose={() =>
						this.setState({ showBlueprintUpdateDialog: false })
					}>
					<DialogTitle>
						{t("updateTrajectoryVersionTitle")}
					</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t("updateTrajectoryVersionMessage")}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							variant="outlined"
							onClick={() =>
								this.setState({
									showBlueprintUpdateDialog: false
								})
							}>
							{t("cancel")}
						</Button>
						<Button
							variant="contained"
							autoFocus
							onClick={() => {
								this.setState({
									showBlueprintUpdateDialog: false
								});
								this.state.info?.id &&
									updateBlueprintVersion(
										this.state.info.id
									).then(() => this.loadTrajectory());
							}}>
							{t("confirm")}
						</Button>
					</DialogActions>
				</Dialog>
			</>
		);
	}
}

function withRouter<ComponentProps>(
	Component: React.ComponentType<ComponentProps>
) {
	function ComponentWithRouterProp(
		props: Omit<ComponentProps, keyof RouteComponentProps>
	) {
		const location = useLocation();
		const navigate = useNavigate();
		const params = useParams();

		return (
			<Component
				{...(props as ComponentProps)}
				location={location}
				navigate={navigate}
				params={params}
			/>
		);
	}

	return ComponentWithRouterProp;
}

export default withTranslation()(withRouter(TrajectoryView));
